//страница "Документы продаж"
import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Form,
  Input,
  Button,
  Table,
  Switch,
  Popconfirm,
  message,
  Select,
  DatePicker,
  Popover,
  Tag,
  InputNumber,
  Radio,
} from "antd";
import {
  DeleteOutlined,
  CloseCircleOutlined,
  CheckCircleOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import axios from "axios";
import moment from "moment"; // Добавлен импорт moment
import { EditDocsSales, PrintedFormDocs } from "src/features/Modal";
import { apiRequests } from "src/shared/api/api";
import {
  ContragentAutocomplete,
  DebounceSelectFil,
  OrganizationAutoComplete,
  WareHousesAutocomplete,
} from "../../shared";
import CreateDocsSalesWrapper from "../../features/Modal/ui/CreateDocsSalesWrapper";
import DocsSalesCalendar from "../KanbanDocsSales/DocsSalesCalendar";

const statusOptions = [
  { label: 'Новый заказ', value: 'received' },
  { label: 'В обработке', value: 'processed' },
  { label: 'Сборка начата', value: 'collecting' },
  { label: 'Собран', value: 'collected' },
  { label: 'Передан курьеру', value: 'picked' },
  { label: 'Доставлен', value: 'delivered' },
  { label: 'Успешно завершен', value: 'success' },
  { label: 'Закрыт', value: 'closed' },
];

const { RangePicker } = DatePicker;

const EditableContext = React.createContext(null);

const EditableRow = ({ index, ...props }) => {
  const [form] = Form.useForm();
  return (
    <Form form={form} component={false}>
      <EditableContext.Provider value={form}>
        <tr {...props} />
      </EditableContext.Provider>
    </Form>
  );
};

const EditableCell = ({
  title,
  editable,
  children,
  dataIndex,
  record,
  handleSave,
  ...restProps
}) => {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef(null);
  const form = useContext(EditableContext);
  useEffect(() => {
    if (editing) {
      inputRef.current.focus();
    }
  }, [editing]);

  const toggleEdit = () => {
    setEditing(!editing);
    form.setFieldsValue({
      [dataIndex]: record[dataIndex],
    });
  };

  const save = async () => {
    try {
      const values = await form.validateFields();
      toggleEdit();
      handleSave({ ...record, ...values });
    } catch (errInfo) {
      console.log("Save failed:", errInfo);
    }
  };

  let childNode = children;

  if (editable) {
    childNode = editing ? (
      <Form.Item
        style={{
          margin: 0,
        }}
        name={dataIndex}
        rules={[
          {
            required: true,
            message: `${title} обязательно для ввода.`,
          },
        ]}
      >
        <Input ref={inputRef} onPressEnter={save} onBlur={save} />
      </Form.Item>
    ) : (
      <div
        className="editable-cell-value-wrap"
        style={{
          paddingRight: 24,
        }}
        onClick={toggleEdit}
      >
        {children}
      </div>
    );
  }

  return <td {...restProps}>{childNode}</td>;
};

class DocsSales extends React.Component {
  constructor(props) {
    super(props);

    this.formRef = React.createRef();

    this.columns = [
      {
        title: "Номер",
        dataIndex: "number",
        key: "number",
        // width: 260,
        editable: false,
        render: (number, record) => {
          let color = 'default';
          
          if (record.color_status) {
            color = record.color_status;
          } 
          else if (record.has_contragent && record.has_loyality_card) {
            color = 'green';
          } else if (record.has_contragent) {
            color = 'blue';
          }
          
          const tagColor = 
            color === 'green' ? 'success' :
            color === 'blue' ? 'processing' :
            'default';
          
          // Формируем информацию для всплывающей подсказки
          const popoverContent = (
            <div>
              <p><strong>Дата:</strong> {record.dated ? new Date(record.dated * 1000).toLocaleDateString() : 'Не указана'}</p>
              <p><strong>Сумма:</strong> {record.sum} ₽</p>
              {record.contragent_name && <p><strong>Контрагент:</strong> {record.contragent_name}</p>}
              <p><strong>Статус оплаты:</strong> {
                record.paid_doc >= (record.sum - record.doc_discount) ? 
                <span style={{ color: 'green' }}>Оплачен</span> : 
                <span style={{ color: 'red' }}>Не оплачен</span>
              }</p>
              <p><strong>Т товаров:</strong> {record.nomenclature_count}</p>
              {record.paid_loyality > 0 && <p><strong>Оплачено бонусами:</strong> {record.paid_loyality} ₽</p>}
            </div>
          );
          
          return (
            <Popover 
              content={popoverContent} 
              title={`Информация о продаже №${number}`}
              placement="right"
            >
              <Tag color={tagColor}>{number}</Tag>
            </Popover>
          );
        },
      },
      {
        title: "Приоритет",
        dataIndex: "priority",
        key: "priority",
        sorter: (a, b) => a.priority - b.priority,
        editable: false,
        width: 100,
        render: priority => {
          let color = 'default';
          if (priority >= 8) color = 'red';
          else if (priority >= 5) color = 'orange';
          return <Tag color={color}>{priority ?? '—'}</Tag>;
        }
      },
      {
        title: "Дата проведения",
        key: "dated",
        dataIndex: "dated",
        // width: 160,
        editable: false,
        render: (date) => {
          const dateFormat = new Date(date * 1000);
          return `
                    ${dateFormat.getDate()}.${
            dateFormat.getMonth() + 1
          }.${dateFormat.getFullYear()}
                    ${dateFormat.getHours()}:${dateFormat.getMinutes()}:${dateFormat.getSeconds()}
                    `;
        },
      },
      {
        title: "Статус",
        key: "status",
        dataIndex: "status",
        // width: 160,
        editable: false,
        render: (checked, row) => {
          return (
            <Switch
              style={{ marginLeft: "13px" }}
              checked={checked}
              onClick={(checked) => this.handleChangeStatus(checked, row)}
            />
          );
        },
      },
      {
        title: "Оплачен полностью",
        key: "full_paid",
        dataIndex: "full_paid",
        // width: 400,
        editable: false,
        render: (checked, row) => {
          if (row.paid_doc >= row.sum - row.doc_discount) {
            return (
              <font style={{ color: "green" }}>
                <CheckCircleOutlined /> Оплачено
              </font>
            );
          }
          return (
            <font style={{ color: "red" }}>
              <CloseCircleOutlined /> Не оплачено
            </font>
          );
        },
      },
      {
        title: "Сумма",
        key: "sum",
        dataIndex: "sum",
        // width: 400,
        editable: false,
      },
    ];

    if (props.amo_widget === undefined || props.amo_widget !== true) {
      this.columns.push(
        {
          title: (
            <>
              Скидка<br/>(на чек)
            </>
          ),
          key: "doc_discount",
          dataIndex: "doc_discount",
          // width: 400,
          editable: false,
        },
        {
          title: (
            <>
              Товаров<br/>(в чеке)
            </>
          ),
          key: "nomenclature_count",
          dataIndex: "nomenclature_count",
          // width: 400,
          editable: false,
        },
        {
          title: "Оплачено",
          key: "paid_doc",
          dataIndex: "paid_doc",
          // width: 400,
          editable: false,
        },
        {
          title: (
            <>
              Оплачено<br/>бонусами
            </>
          ),
          key: "paid_loyality",
          dataIndex: "paid_loyality",
          // width: 400,
          editable: false,
        },
        {
          title: (
            <>
              Оплачено<br/>рублями
            </>
          ),
          key: "paid_rubles",
          dataIndex: "paid_rubles",
          // width: 400,
          editable: false,
        }
      );
    }

    this.columns.push({
      title: "Действие",
      key: "action",
      // width: 100,
      width: 160,
      render: (_, record) => {
        return this.state.dataSource.length >= 1 ? (
          <>
            <Popconfirm
              title="Подтвердите удаление"
              onConfirm={() => this.handleDelete(record.id)}
              cancelText="Отмена"
              okText="OK"
            >
              <Button icon={<DeleteOutlined />} style={{ marginRight: 10 }} />
            </Popconfirm>
            <EditDocsSales
              doc={record}
              token={this.props.query.token}
              updateRow={this.updateRow}
              tags={this.props.tags}
            />
            <PrintedFormDocs token={this.props.query.token} record={record} />
          </>
        ) : (
          "Загрузка..."
        );
      },
    });

    this.state = {
      count: this.props.c,
      dataSource: this.props.ds,
      loading: true,
      currentPage: 1,
      filterCollapsed: false,
      viewMode: 'table',
      pickersMeta: [],
      couriersMeta: [],
      filters: this.getInitialFiltersFromUrl(),
    };
  }

  getInitialFiltersFromUrl = () => {
    const params = new URLSearchParams(window.location.search);
    const filters = {};
    
    // Преобразуем параметры URL в объект фильтров
    params.forEach((value, key) => {
      if (value && key !== 'token') {
        // Обработка множественных значений (например, теги)
        if (key === 'tags' || key === 'operation' || key === 'comment' || key === 'order_status') {
          filters[key] = value.split(',');
        } 
        // Обработка булевых значений
        else if (value === 'true' || value === 'false') {
          filters[key] = value === 'true';
        }
        // Обработка чисел
        else if (!isNaN(value)) {
          filters[key] = Number(value);
        }
        // Обработка дат (если они в формате YYYY-MM-DD)
        else if (key.includes('_date') && moment(value, 'YYYY-MM-DD', true).isValid()) {
          filters[key] = moment(value, 'YYYY-MM-DD');
        }
        // Остальные строковые значения
        else {
          filters[key] = value;
        }
      }
    });
    
    return filters;
  };

  // Обновление URL с текущими фильтрами
  updateUrlWithFilters = (filters) => {
    const params = new URLSearchParams();
    
    // Добавляем токен в URL в любом случае
    params.set('token', this.props.query.token);
    
    // Добавляем параметры фильтров, если они есть
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (Array.isArray(value)) {
          if (value.length > 0) {
            params.set(key, value.join(','));
          }
        } else if (moment.isMoment(value)) {
          params.set(key, value.format('YYYY-MM-DD'));
        } else if (typeof value === 'boolean') {
          params.set(key, value.toString());
        } else {
          params.set(key, value.toString());
        }
      }
    });
    
    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.pushState(null, '', newUrl);
    
    // Принудительно обновляем состояние, чтобы отразить изменения в UI
    this.setState({ filters });
  };

  resetFilters = () => {
    // Сбрасываем значения формы
    this.formRef.current.resetFields();
    
    // Создаем пустой объект фильтров
    const emptyFilters = {};
    
    // Обновляем состояние и URL, затем выполняем запрос
    this.setState({ filters: emptyFilters }, () => {
      // Очищаем URL от параметров фильтров
      const cleanUrl = `${window.location.pathname}?token=${this.props.query.token}`;
      window.history.pushState(null, '', cleanUrl);
      
      // Выполняем запрос без фильтров
      this.fetch(
        1,
        `https://${process.env.REACT_APP_APP_URL}/api/v1/docs_sales/`,
        {}
      );
    });
    
    // Показываем сообщение о сбросе фильтров
    message.success({ content: "Фильтры сброшены", duration: 2 });
  };

  handleViewModeChange = (e) => {
    const newMode = e.target.value;
    
    this.setState({ 
      viewMode: newMode
    }, () => {
      // Перезапрашиваем данные с текущими фильтрами
      this.fetch(
        1,
        `https://${process.env.REACT_APP_APP_URL}/api/v1/docs_sales/`,
        this.state.filters
      );
    });
    
    // Обновление URL с параметром kanban
    const searchParams = new URLSearchParams(window.location.search);
    if (newMode === 'calendar') {
      searchParams.set('kanban', 'true');
    } else {
      searchParams.delete('kanban');
    }
    
    const newUrl = `${window.location.pathname}?${searchParams.toString()}`;
    window.history.pushState(null, '', newUrl);
  };

  componentDidMount() {
    window.addEventListener('popstate', this.handlePopState);
  
    // Определяем начальные фильтры в зависимости от режима
    const searchParams = new URLSearchParams(window.location.search);
    const initialFilters = this.state.filters;
    
    // Убрана установка фильтра has_delivery
    if (searchParams.get('kanban') === 'true') {
      this.setState({ 
        viewMode: 'calendar'
      });
    }
    
    // Применяем начальные фильтры из URL
    if (Object.keys(initialFilters).length > 0) {
      this.filterHandler(initialFilters);
    } else {
      this.fetch(
        1,
        `https://${process.env.REACT_APP_APP_URL}/api/v1/docs_sales/`,
        initialFilters
      );
    }

    
    const { websocket } = this.props;

    websocket.onmessage = (message) => {
      try {
        const data = JSON.parse(message.data);
        if (data.target !== "docs_sales") return;

        // В зависимости от action — перезапросим данные
        // create -> перезагрузить первую страницу (чтобы новый элемент гарантированно виден)
        // edit/update_status/delete -> перезагрузить текущую страницу
        if (data.action === "create") {
          // если фильтры применены — сохраняем их, но переходим на первую страницу
          this.setState({ loading: true, currentPage: 1 }, () => {
            this.fetch(1, `https://${process.env.REACT_APP_APP_URL}/api/v1/docs_sales/`, this.state.filters);
          });
        } else if (data.action === "edit" || data.action === "update_status" || data.action === "delete") {
          const page = this.state.currentPage || 1;
          this.setState({ loading: true }, () => {
            this.fetch(page, `https://${process.env.REACT_APP_APP_URL}/api/v1/docs_sales/`, this.state.filters);
          });
        }
      } catch (err) {
        console.error('Ошибка разбора websocket сообщения', err);
      }
    };
  }

  componentWillUnmount() {
    // Убираем обработчик при размонтировании компонента
    window.removeEventListener('popstate', this.handlePopState);
  }

  handlePopState = () => {
    const newFilters = this.getInitialFiltersFromUrl();
    this.setState({ filters: newFilters }, () => {
      // Применяем фильтры из URL
      this.filterHandler(newFilters);
    });
  };

  handleChangeStatus = (status, row) => {
    this.edit_request(row.id, row, { status: status });
  };

  fetch = (page, url = {}, filters = {}) => {
    const limit = 35;
    const offset = page * 35 - 35;

    let params = {
      token: this.props.query.token,
      limit: limit,
      offset: offset,
      ...filters,
    };

    if (this.props.tags !== undefined) {
      params.tags = this.props.tags;
    }

    axios.get(url, { params: params }).then((response) => {
      // raw — явная ссылка на исходный массив результатов
      const raw = response.data.result || [];

      // нормализованные данные для таблицы
      const newData = raw.map((rowData) => ({
        created_at: Date.now(),
        updated_at: Date.now(),
        response_code: 200,
        response: JSON.stringify(rowData, null, 3),
        ...rowData,
      }));

      // --- собираем уникальных сборщиков и курьеров из raw ---
      const pickersMap = new Map();
      const couriersMap = new Map();

      raw.forEach((doc) => {
        const p = doc.assigned_picker;
        if (p !== null && p !== undefined) {
          if (typeof p === 'object') {
            const pickerId = p.id;
            const pickerName = [p.last_name, p.first_name].filter(Boolean).join(' ') || 
                              p.full_name || 
                              p.label || 
                              `ID ${pickerId}`;
            if (pickerId !== null) {
              pickersMap.set(String(pickerId), { 
                label: pickerName, 
                value: pickerId 
              });
            }
          } else {
            // Если это просто ID, создаем запись с placeholder именем
            pickersMap.set(String(p), { 
              label: `ID ${p}`, 
              value: p 
            });
          }
        }

        const c = doc.assigned_courier;
        if (c !== null && c !== undefined) {
          if (typeof c === 'object') {
            const courierId = c.id;
            const courierName = [c.last_name, c.first_name].filter(Boolean).join(' ') || 
                                c.full_name || 
                                c.label || 
                                `ID ${courierId}`;
            if (courierId !== null) {
              couriersMap.set(String(courierId), { 
                label: courierName, 
                value: courierId 
              });
            }
          } else {
            // Если это просто ID, создаем запись с placeholder именем
            couriersMap.set(String(c), { 
              label: `ID ${c}`, 
              value: c 
            });
          }
        }
      });

      const pickersMeta = Array.from(pickersMap.values());
      const couriersMeta = Array.from(couriersMap.values());

      // --- остальная существующая логика (тэги, operationsMeta, commentsMeta) ---
      if (response.data.result && response.data.result.length > 0) {
        let return_tags = response.data.result
          .map((doc) => doc.tags?.split(","))
          .flat(1)
          .filter((item, pos, self) => self.indexOf(item) === pos)
          .map((value, i) => ({ label: value, value: i }));
        this.setState({ tagsMeta: return_tags });

        let res2 = response.data.result
          .map((doc) => doc.operation)
          .filter((item, pos, self) => self.indexOf(item) === pos)
          .map((value, i) => ({ label: value, value: i }));
        this.setState({ operationsMeta: res2 });

        let res3 = response.data.result
          .map((doc) => doc.comment)
          .filter((item, pos, self) => self.indexOf(item) === pos)
          .map((value, i) => ({ label: value, value: i }));
        this.setState({ commentsMeta: res3 });
      }

      // Обновляем state единственным setState вызовом (чтобы избежать лишних ререндеров)
      this.setState({
        count: response.data.count,
        dataSource: newData,
        loading: false,
        pickersMeta,
        couriersMeta,
      });
    });
  };

  handleDelete = (id) => {
    const dataSource = [...this.state.dataSource];
    const row_index = dataSource.findIndex((item) => item.id === id);
    const row = dataSource[row_index];
    dataSource.splice(row_index, 1);

    this.setState({
      dataSource: dataSource,
    });

    axios
      .delete(
        `https://${process.env.REACT_APP_APP_URL}/api/v1/docs_sales/${row.id}/`,
        {
          params: { token: this.props.query.token },
        }
      )
      .then((response) => {
        message.success("Вы успешно удалили документ продажи");
      })
      .catch((err) => {
        message.error("Не удалось удалить документ продажи!");
        console.log("err", err);
      });
  };

  edit_request = (id, payment, row) => {
    let edit_dict = {};

    for (let item in row) {
      if (row[item] !== payment[item]) {
        edit_dict[item] = row[item];
      }
    }

    edit_dict.id = id;
    edit_dict.organization = payment.organization;

    if (Object.keys(edit_dict).length !== 0) {
      apiRequests.docs.patchSales(id, [edit_dict]);
    } else {
      message.error(<>Вы не сделали никаких изменений!</>);
    }
  };

  handleSave = (row) => {
    const newData = [...this.state.dataSource];
    const index = newData.findIndex((item) => row.id === item.id);
    const item = newData[index];

    newData.splice(index, 1, { ...item, ...row });

    this.setState({
      dataSource: newData,
    });

    this.edit_request(newData.splice(index, 1, { ...item, ...row })[0]);
  };
  
  convertToApiParams = (values) => {
    const params = { ...values };
    
    // Преобразование дат в timestamp
    if (params.dated) {
      params.dated_from = params.dated[0].unix();
      params.dated_to = params.dated[1].unix();
      delete params.dated;
    }
    
    if (params.created_at) {
      params.created_at_from = params.created_at[0].unix();
      params.created_at_to = params.created_at[1].unix();
      delete params.created_at;
    }
    
    if (params.updated_at) {
      params.updated_at_from = params.updated_at[0].unix();
      params.updated_at_to = params.updated_at[1].unix();
      delete params.updated_at;
    }
    
    if (params.delivery_date) {
      params.delivery_date_from = params.delivery_date[0].unix();
      params.delivery_date_to = params.delivery_date[1].unix();
      delete params.delivery_date;
    }
    
    // Обработка выбранных пользователей (менеджер, создатель)
    if (params.sales_manager && params.sales_manager.value) {
      params.sales_manager = params.sales_manager.value;
    }
    
    if (params.created_by && params.created_by.value) {
      params.created_by = params.created_by.value;
    }
    
    // Обработка сборщика и курьера
    if (params.picker) {
      if (typeof params.picker === 'object' && params.picker.value) {
        params.picker_id = params.picker.value;
      } else {
        params.picker_id = params.picker;
      }
      delete params.picker;
    }

    if (params.courier) {
      if (typeof params.courier === 'object' && params.courier.value) {
        params.courier_id = params.courier.value;
      } else {
        params.courier_id = params.courier;
      }
      delete params.courier;
    }
    
    // Обработка специальных фильтров (доставка, сборщик, курьер)
    ['has_delivery', 'has_picker', 'has_courier'].forEach((key) => {
      if (params[key] === 'any') {
        delete params[key];
      }
    });
    
    // Преобразование массива статусов в строку
    if (params.order_status && Array.isArray(params.order_status)) {
      params.order_status = params.order_status.join(',');
    }
    
    // Преобразование тегов, операций и комментариев
    if (params.tags && Array.isArray(params.tags)) {
      params.tags = params.tags
        .map((item) => (item.label ? item.label : item.value))
        .join(',');
    }
    
    if (params.operation && Array.isArray(params.operation)) {
      params.operation = params.operation
        .map((item) => (item.label ? item.label : item.value))
        .join(',');
    }
    
    if (params.comment && Array.isArray(params.comment)) {
      params.comment = params.comment
        .map((item) => (item.label ? item.label : item.value))
        .join(',');
    }
    
    // Удаление пустых значений
    Object.keys(params).forEach((key) => {
      if (
        params[key] === undefined ||
        params[key] === null ||
        params[key] === '' ||
        (Array.isArray(params[key]) && params[key].length === 0)
      ) {
        delete params[key];
      }
    });
    
    return params;
  };

  filterHandler = async (values) => {
    ['has_delivery', 'has_picker', 'has_courier'].forEach((k) => {
      if (values[k] === 'any') {
        delete values[k];
      }
    });
    
    message.loading({
      content: "Поиск...",
    });
    
    // Сначала обновляем URL и состояние
    this.setState({ filters: values }, () => {
      this.updateUrlWithFilters(values);
    });
    
    // Затем преобразуем значения для API и выполняем запрос
    const apiParams = this.convertToApiParams(values);
    this.fetch(1, `https://${process.env.REACT_APP_APP_URL}/api/v1/docs_sales/`, apiParams);
    
    message.destroy();
    message.success({
      content: "Поиск завершен",
      duration: 1,
    });
  };

  fetchManager = async (name) => {
    return fetch(
      `https://${process.env.REACT_APP_APP_URL}/api/v1/cashbox_users/?token=${this.props.query.token}`
    )
      .then((response) => response.json())
      .then((body) => {
        return body;
      })
      .then((body) => {
        if (body.result) {
          return body.result.map((user) => ({
            label: `${user.first_name} ${user.last_name}`,
            value: user.id,
          }));
        }
      })
      .then((body) => {
        return body;
      });
  };

  fetchTags = async (name) => {
    if (name) {
      return fetch(
        `https://${process.env.REACT_APP_APP_URL}/api/v1/docs_sales/?token=${this.props.query.token}&tags=${name}`
      )
        .then((response) => response.json())
        .then((body) => {
          return body;
        })
        .then((body) => {
          if (body.result && body.result.length > 0) {
            let res = body.result.map((doc) => doc.tags.split(",")).flat(1);
            let tags = res.filter(
              (item) => item.toUpperCase().indexOf(name.toUpperCase()) !== -1
            );
            let return_tags = tags.map((value, i) => {
              return {
                label: value,
                value: i,
              };
            });
            this.setState({ tagsMeta: return_tags });
            return return_tags;
          }
        })
        .then((body) => {
          return body;
        });
    }
  };

  fetchOperations = async (name) => {
    if (name) {
      return fetch(
        `https://${process.env.REACT_APP_APP_URL}/api/v1/docs_sales/?token=${this.props.query.token}&operation=${name}`
      )
        .then((response) => response.json())
        .then((body) => {
          return body;
        })
        .then((body) => {
          if (body.result && body.result.length > 0) {
            let res = body.result
              .map((doc) => doc.operation)
              .filter(
                (item) => item.toUpperCase().indexOf(name.toUpperCase()) !== -1
              )
              .map((value, i) => {
                return {
                  label: value,
                  value: i,
                };
              });
            this.setState({ operationsMeta: res });
            return res;
          }
        })
        .then((body) => {
          return body;
        });
    }
  };

  fetchComments = async (name) => {
    if (name) {
      return fetch(
        `https://${process.env.REACT_APP_APP_URL}/api/v1/docs_sales/?token=${this.props.query.token}&comment=${name}`
      )
        .then((response) => response.json())
        .then((body) => {
          return body;
        })
        .then((body) => {
          if (body.result && body.result.length > 0) {
            let res = body.result
              .map((doc) => doc.comment)
              .filter(
                (item) => item.toUpperCase().indexOf(name.toUpperCase()) !== -1
              )
              .map((value, i) => {
                return {
                  label: value,
                  value: i,
                };
              });
            this.setState({ commentsMeta: res });
            return res;
          }
        })
        .then((body) => {
          return body;
        });
    }
  };

  updateRow = (data, response) => {
    // защита: если вызов пришёл без response — попробуем взять из data
    const res = response || {};
    const resData = (res.data) ? res.data : data;
    const id = resData?.id ?? data?.id;

    // определим, создание это или редактирование.
    // если response.status === 201 или объект не найден в текущем dataSource => считаем созданием
    const existsInState = this.state.dataSource.some(item => String(item.id) === String(id));
    const isCreate = (res.status === 201) || (!existsInState && !!id);

    // переключим загрузочный индикатор и выполним fetch:
    if (isCreate) {
      // при создании логично показывать первую страницу, чтобы пользователь увидел новый документ
      this.setState({ loading: true, currentPage: 1 }, () => {
        this.fetch(1, `https://${process.env.REACT_APP_APP_URL}/api/v1/docs_sales/`, this.state.filters);
        message.success('Документ создан — данные обновлены');
      });
    } else {
      // при редактировании — перезапросим текущую страницу, сохранив фильтры и номер страницы
      const page = this.state.currentPage || 1;
      this.setState({ loading: true }, () => {
        this.fetch(page, `https://${process.env.REACT_APP_APP_URL}/api/v1/docs_sales/`, this.state.filters);
        message.success('Документ обновлён — данные обновлены');
      });
    }
  };

  findContragent = async (id) => {
    return fetch(
      `https://${process.env.REACT_APP_APP_URL}/api/v1/contragents/${id}/?token=${this.props.query.token}`
    )
      .then((response) => response.json())
      .then((body) => {
        return body;
      });
  };

  onSelectCa = (val) => {
    this.findContragent(val).then((res) => {
      this.setState({ current_contragent: res });
      this.formRef.current.setFieldsValue({
        contragent: res.id,
      });
    });
  };

  render() {
    const { dataSource } = this.state;
    const components = {
      body: {
        row: EditableRow,
        cell: EditableCell,
      },
    };
    const columns = this.columns.map((col) => {
      if (!col.editable) {
        return col;
      }

      return {
        ...col,
        onCell: (record) => ({
          record,
          editable: col.editable,
          dataIndex: col.dataIndex,
          title: col.title,
          handleSave: this.handleSave,
        }),
      };
    });

     return (
      <div>
        {/* Исправленная верхняя панель */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
          flexWrap: 'nowrap',
          padding: '8px 0',
          borderBottom: '1px solid #f0f0f0',
          gap: 16
        }}>
          {/* Левая группа: кнопка и переключатель */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'nowrap',
            gap: 8,
            minWidth: 0,
            flex: '1 1 auto'
          }}>
            <CreateDocsSalesWrapper
              token={this.props.query.token}
              updateRow={this.updateRow}
              tags={this.props.tags}
              phone={this.props.phone}
              name={this.props.name}
            />
            
            <Radio.Group 
              value={this.state.viewMode}
              onChange={this.handleViewModeChange}
              buttonStyle="solid"
              style={{ flexShrink: 0 }}
            >
              <Radio.Button value="table">Таблица</Radio.Button>
              <Radio.Button value="calendar">Календарь</Radio.Button>
            </Radio.Group>
          </div>
          
          {/* Правая группа: кнопка фильтров */}
          <Button 
            icon={<SearchOutlined />}
            onClick={() => this.setState({ filterCollapsed: !this.state.filterCollapsed })}
            type={this.state.filterCollapsed ? "primary" : "default"}
            style={{ flexShrink: 0 }}
          >
            {this.state.filterCollapsed ? "Скрыть фильтры" : "Показать фильтры"}
          </Button>
        </div>


        {/* Блок фильтров с условным рендерингом */}
        {this.state.filterCollapsed && (
          <div style={{ 
            marginBottom: 16, 
            padding: 16, 
            border: '1px solid #d9d9d9', 
            borderRadius: 4,
            backgroundColor: '#fff',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <Form
              ref={this.formRef}
              layout={"inline"}
              onFinish={this.filterHandler}
            >
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', 
                gap: 16,
                width: '100%'
              }}>
                <Form.Item name="priority" style={{ margin: 0 }}>
                  <InputNumber 
                    placeholder="Приоритет" 
                    min={0} 
                    max={10} 
                    style={{ width: '100%' }} 
                  />
                </Form.Item>

                 {/* Добавлен блок выбора статусов */}
                <Form.Item name="order_status" style={{ margin: 0 }}>
                  <Select
                    options={statusOptions}
                    placeholder="Статус заказа"
                    allowClear={true}
                    mode="multiple"
                    style={{ width: '100%' }}
                  />
                </Form.Item>

                <Form.Item name="has_delivery" style={{ margin: 0 }}>
                  <Select
                    placeholder="Доставка"
                    allowClear
                    style={{ width: '100%' }}
                    options={[
                      { label: 'Любая', value: 'any' },
                      { label: 'Нужна доставка', value: true },
                      { label: 'Без доставки', value: false },
                    ]}
                  />
                </Form.Item>

                <Form.Item name="has_picker" style={{ margin: 0 }}>
                  <Select
                    placeholder="Сборщик"
                    allowClear
                    style={{ width: '100%' }}
                    options={[
                      { label: 'Любой', value: 'any' },
                      { label: 'Назначен', value: true },
                      { label: 'Не назначен', value: false },
                    ]}
                  />
                </Form.Item>

                <Form.Item name="has_courier" style={{ margin: 0 }}>
                  <Select
                    placeholder="Курьер"
                    allowClear
                    style={{ width: '100%' }}
                    options={[
                      { label: 'Любой', value: 'any' },
                      { label: 'Назначен', value: true },
                      { label: 'Не назначен', value: false },
                    ]}
                  />
                </Form.Item>
                
                <Form.Item name="tags" style={{ margin: 0 }}>
                  <Select
                    options={this.state.tagsMeta}
                    placeholder="Теги"
                    allowClear={true}
                    showSearch
                    labelInValue
                    filterOption={false}
                    mode={"tags"}
                    onSearch={this.fetchTags}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
                
                <Form.Item name="operation" style={{ margin: 0 }}>
                  <Select
                    options={this.state.operationsMeta}
                    placeholder="Тип операции"
                    allowClear={true}
                    showSearch
                    labelInValue
                    filterOption={false}
                    mode={"tags"}
                    onSearch={this.fetchOperations}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
                
                <Form.Item name="comment" style={{ margin: 0 }}>
                  <Select
                    options={this.state.commentsMeta}
                    placeholder="Комментарий"
                    allowClear={true}
                    showSearch
                    labelInValue
                    filterOption={false}
                    mode={"tags"}
                    onSearch={this.fetchComments}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
                
                <Form.Item name="contragent" style={{ margin: 0 }}>
                  <ContragentAutocomplete
                    api={`https://${process.env.REACT_APP_APP_URL}/api/v1/`}
                    token={this.props.query.token}
                    placeholder="Контрагент"
                    allowClear={true}
                    width="100%"
                    onSelect={this.onSelectCa}
                    сontragent={this.state.current_contragent}
                  />
                </Form.Item>
                
                <Form.Item name="organization" style={{ margin: 0 }}>
                  <OrganizationAutoComplete
                    api={`https://${process.env.REACT_APP_APP_URL}/api/v1/`}
                    token={this.props.query.token}
                    labelInValue
                    placeholder="Организация"
                    nodefaultvalue={"true"}
                    allowClear={true}
                    onSelect={(obj) =>
                      this.formRef.current.setFieldValue(
                        "organization",
                        obj.value
                      )
                    }
                    style={{ width: '100%' }}
                  />
                </Form.Item>
                
                <Form.Item name="warehouse" style={{ margin: 0 }}>
                  <WareHousesAutocomplete
                    api={`https://${process.env.REACT_APP_APP_URL}/api/v1/`}
                    token={this.props.query.token}
                    labelInValue
                    placeholder="Склад"
                    nodefaultvalue={"true"}
                    allowClear={true}
                    onSelect={(obj) =>
                      this.formRef.current.setFieldValue(
                        "warehouse",
                        obj.value
                      )
                    }
                    style={{ width: '100%' }}
                  />
                </Form.Item>
                
                <Form.Item name="sales_manager" style={{ margin: 0 }}>
                  <DebounceSelectFil
                    placeholder="Менеджер"
                    fetchOptions={this.fetchManager}
                    removeIcon={null}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
                
                <Form.Item name="created_by" style={{ margin: 0 }}>
                  <DebounceSelectFil
                    placeholder="Создатель"
                    fetchOptions={this.fetchManager}
                    removeIcon={null}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </div>
              
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', 
                gap: 16,
                width: '100%',
                marginTop: 16
              }}>
                <Form.Item name="dated" style={{ margin: 0 }}>
                  <RangePicker
                    style={{ width: '100%' }}
                    placeholder={["Начальная дата", "Конечная дата"]}
                    allowClear={true}
                    format="DD.MM.YY"
                  />
                </Form.Item>
                
                <Form.Item name="created_at" style={{ margin: 0 }}>
                  <RangePicker
                    style={{ width: '100%' }}
                    placeholder={["Создан от", "Создан до"]}
                    allowClear={true}
                    format="DD.MM.YY"
                  />
                </Form.Item>
                
                <Form.Item name="updated_at" style={{ margin: 0 }}>
                  <RangePicker
                    style={{ width: '100%' }}
                    placeholder={["Обновлен от", "Обновлен до"]}
                    allowClear={true}
                    format="DD.MM.YY"
                  />
                </Form.Item>

                <Form.Item name="delivery_date" style={{ margin: 0 }}>
                  <RangePicker
                    style={{ width: '100%' }}
                    placeholder={["Доставка от", "Доставка до"]}
                    allowClear={true}
                    format="DD.MM.YY"
                  />
                </Form.Item>
              </div>
              
              <div style={{ 
                display: 'flex', 
                justifyContent: 'flex-end', 
                marginTop: 16,
                gap: 8
              }}>
                <Button
                  onClick={this.resetFilters}
                  style={{ marginRight: 8 }}
                >
                  Сбросить
                </Button>
                <Button
                  htmlType="submit"
                  icon={<SearchOutlined />}
                  type="primary"
                >
                  Поиск
                </Button>
              </div>
            </Form>
          </div>
        )}

        {/* Основной контент - таблица или календарь */}
        {this.state.viewMode === 'table' ? (
          <Table
            components={components}
            rowClassName={(record) => record.is_deleted && "disabled-row"}
            rowKey={(record) => record.id}
            bordered
            loading={this.state.loading}
            dataSource={dataSource}
            columns={columns}
            scroll={{ x: 'max-content' }}
            pagination={{
              total: this.state.count,
              onChange: (page) => {
                this.setState(
                  { currentPage: page, loading: true },
                  this.fetch(
                    page,
                    `https://${process.env.REACT_APP_APP_URL}/api/v1/docs_sales/`,
                    this.state.filters
                  )
                );
              },
              pageSize: 35,
              showSizeChanger: false,
              showTotal: (total) => `Всего ${total} записей`,
            }}
          />
        ) : (
          <DocsSalesCalendar
            dataSource={this.state.dataSource}
            token={this.props.query.token}
            updateRow={this.updateRow}
            visibleStatuses={Array.isArray(this.state.filters.order_status) ? 
              this.state.filters.order_status : 
              (this.state.filters.order_status ? this.state.filters.order_status.split(',') : [])
            }
          />
        )}
      </div>
    );
  }
}

export default DocsSales;